import * as React from 'react';

import { useLockFn } from 'ahooks';
import { Flex, Form, notification } from 'antd';
import { useNavigate } from 'react-router-dom';

import { PrimitiveForm } from 'components';
import { useShippingConfigValue } from 'context';
import { createShippingConfig, updateShippingConfig } from 'services';
import type { SelectOption, TreeSelectSource } from 'types';
import { fixedParseFloat, normalizeToString, PATHNAME } from 'utils';

import { ShippingConfigFormAction } from './action';
import { ShippingConfigFormContent } from './content';

const {
  LOCAL: {
    SHIPPING_CONFIGS: { BASE_URL: SHIPPING_CONFIG_BASE_URL },
  },
} = PATHNAME;

type FieldType = Pick<
  ReturnType<typeof useShippingConfigValue>,
  'title' | 'amountType' | 'amount'
> &
  Partial<
    Record<'products' | 'brands', SelectOption[]> &
      Record<'categories', TreeSelectSource[]> &
      Record<'cities', TreeSelectSource[]>
  >;

const ShippingConfigForm = () => {
  const [form] = Form.useForm<FieldType>();
  const {
    id = '',
    amountType,
    amount,
    defaultTitle,
    mode,
  } = useShippingConfigValue();
  const navigate = useNavigate();

  const formattedAmount = React.useMemo<number | undefined>(() => {
    const isPercentageType = amountType === 'Percentage';
    const isNumberAmount = typeof amount === 'number';

    if (!isNumberAmount) return;

    const fixedAmount = fixedParseFloat(amount, 3);
    const amountValue = Math.min(Math.max(0, fixedAmount), 100);

    if (!isPercentageType) return fixedAmount;

    return amountValue;
  }, [amountType, amount]);

  const onFinish = useLockFn(
    async ({
      title = '',
      products = [],
      brands = [],
      categories = [],
      cities = [],
    }: FieldType) => {
      const isUpdatedMode = mode === 'Update';

      const params: Parameters<typeof updateShippingConfig>[0] = {
        id,
      };
      const body: Parameters<typeof updateShippingConfig>[1] = {
        title,
        amountType: amountType!,
        amount: formattedAmount || 0,
        productsIds: products.map(({ value }) => normalizeToString(value)),
        brandsIds: brands.map(({ value }) => normalizeToString(value)),
        categoriesIds: categories.map(({ value }) => normalizeToString(value)),
        citiesIds: cities.map(({ value }) => normalizeToString(value)),
      };

      (isUpdatedMode
        ? updateShippingConfig(params, body)
        : createShippingConfig(body)
      )
        .then(() => {
          notification.success({
            message: `تنظیمات ارسال ${isUpdatedMode ? defaultTitle : title} با موفقیت ${isUpdatedMode ? 'ویرایش' : 'ساخته'} شد.`,
          });
          navigate(SHIPPING_CONFIG_BASE_URL);
        })
        .catch(message => {
          notification.error({ message });
        });
    },
  );

  return (
    <PrimitiveForm
      form={form}
      fields={[
        {
          name: 'amount',
          value: formattedAmount,
        },
      ]}
      onFinish={onFinish}
    >
      <Flex
        className='*:rounded *:bg-white max-xl:flex-col max-xl:last:*:sticky max-xl:last:*:inset-x-0 max-xl:last:*:bottom-0 max-xl:last:*:z-10 max-xl:last:*:shadow-inner xl:items-start xl:first:*:basis-[70%] xl:last:*:basis-[30%]'
        gap='large'
      >
        <ShippingConfigFormContent />
        <ShippingConfigFormAction />
      </Flex>
    </PrimitiveForm>
  );
};

export { ShippingConfigForm };
